import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, PlusSquare, User, Search, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

/**
 * Mobile-only bottom navigation (visible <md, hidden md+).
 *
 * Five primary flows (Home / Explore / Calls / Create / Profile). The
 * central "Chiamate" item is a prominent raised gold button. Labels come
 * from the i18n `header:nav.*` namespace.
 */
const ITEMS = [
  { to: '/', icon: Home, key: 'home', end: true },
  { to: '/explore', icon: Search, key: 'explore' },
  { to: '/calls', icon: Phone, key: 'calls', prominent: true },
  { to: '/create', icon: PlusSquare, key: 'create' },
  { to: '/profile', icon: User, key: 'profile' },
];

const BottomNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation('header');

  // Hide on auth and checkout pages
  if (['/login', '/register'].includes(location.pathname)) return null;
  if (location.pathname.startsWith('/checkout/')) return null;
  if (!user) return null;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] md:hidden"
      data-testid="bottom-nav"
    >
      <div className="flex h-14 items-center justify-around px-2">
        {ITEMS.map(({ to, icon: Icon, key, end, prominent }) => {
          const label = t(`nav.${key}`);
          if (prominent) {
            return (
              <NavLink
                key={to}
                to={to}
                aria-label={label}
                className="flex flex-1 items-center justify-center"
                data-testid={`bottom-nav-${key}`}
              >
                {({ isActive }) => (
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full gold-gradient shadow transition-transform ${
                      isActive ? 'scale-110' : ''
                    }`}
                  >
                    <Icon className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
                  </span>
                )}
              </NavLink>
            );
          }
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              aria-label={label}
              className={({ isActive }) =>
                `flex flex-1 items-center justify-center py-2 ${isActive ? 'gold-text' : 'text-foreground'}`
              }
              data-testid={`bottom-nav-${key}`}
            >
              {({ isActive }) => (
                <Icon
                  className="h-7 w-7"
                  strokeWidth={isActive ? 2.4 : 1.8}
                  aria-hidden="true"
                />
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
